import { useEffect, useRef, useState } from 'react'

export default function HeroSection() {
  const [loaded, setLoaded] = useState(false)
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setLoaded(true)
  }, [])

  const scrollTo = (id: string) => {
    const el = document.querySelector(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-background-50"
    >
      {/* Background Logo Watermark */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/60f21865-9619-478d-8c5a-068ce8f151ff.png"
          alt=""
          className="w-[95vw] md:w-[75vw] max-w-[1100px] opacity-[0.15] object-contain"
        />
      </div>

      {/* Decorative organic curves */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
        <svg
          className="absolute -top-20 -left-20 w-[400px] h-[400px] md:w-[600px] md:h-[600px] opacity-[0.06]"
          viewBox="0 0 400 400"
          fill="none"
        >
          <path
            d="M-20 200C60 120 140 80 220 120C300 160 360 260 340 360"
            stroke="oklch(var(--primary-500))"
            strokeWidth="2"
            fill="none"
          />
          <path
            d="M-40 280C40 200 120 160 200 200C280 240 340 340 320 440"
            stroke="oklch(var(--accent-500))"
            strokeWidth="1.5"
            fill="none"
          />
        </svg>
        <svg
          className="absolute -bottom-20 -right-20 w-[400px] h-[400px] md:w-[600px] md:h-[600px] opacity-[0.05]"
          viewBox="0 0 400 400"
          fill="none"
        >
          <path
            d="M420 180C340 260 260 300 180 260C100 220 40 120 60 20"
            stroke="oklch(var(--primary-500))"
            strokeWidth="2"
            fill="none"
          />
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-6 md:px-10 max-w-4xl mx-auto pt-24 md:pt-28">
        {/* Small label */}
        <div
          className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-500/10 text-primary-700 text-xs md:text-sm font-medium mb-8 transition-all duration-1000 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-primary-500 inline-block" />
          DAON Psychological Counseling Center
        </div>

        {/* Main Title */}
        <h1
          className={`font-heading text-2xl sm:text-3xl md:text-5xl lg:text-6xl text-primary-700 leading-tight md:leading-tight mb-6 whitespace-nowrap transition-all duration-1000 delay-200 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          마음의 회복과 성장을 위한 동행
        </h1>

        {/* Subtitle */}
        <p
          className={`text-base md:text-lg text-foreground-600 max-w-2xl mx-auto mb-10 leading-relaxed transition-all duration-1000 delay-400 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          다온심리상담센터는 건강한 마음과 행복한 삶을 위한
          <br className="hidden md:block" />
          심리 지원 활동을 소개합니다.
        </p>

        {/* Buttons */}
        <div
          className={`flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-1000 delay-600 ${
            loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <button
            onClick={() => scrollTo('#about')}
            className="px-8 py-3 rounded-full bg-primary-600 text-background-50 text-sm md:text-base font-medium hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
          >
            센터 소개
          </button>
          <button
            onClick={() => scrollTo('#activities')}
            className="px-8 py-3 rounded-full border-2 border-primary-600 text-primary-700 text-sm md:text-base font-medium hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            주요 활동
          </button>
        </div>
      </div>

      {/* Bottom decorative image strip */}
      <div className="absolute bottom-0 left-0 right-0 h-[280px] md:h-[380px] pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-t from-background-50/30 via-background-50/15 to-transparent z-10" />
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20peaceful%20interior%20with%20large%20window%20and%20natural%20light%20streaming%20in%2C%20green%20indoor%20plants%20near%20window%2C%20soft%20ivory%20and%20beige%20tones%2C%20cozy%20furniture%20silhouettes%2C%20botanical%20atmosphere%2C%20gentle%20morning%20light%2C%20editorial%20lifestyle%20photography&width=1600&height=500&seq=daon-hero-bottom&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-center"
        />
      </div>
    </section>
  )
}